import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import {
  Backdrop,
  Dialog,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
} from "@ui";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeColours } from "@/clients/acme/config/colours";
import { CircleNumber } from "@/pages/private/dashboard/budgetPage";
import {
  ApiResponse,
  BudgetService,
  DependentsService,
  MarketingService,
  OnboardingService,
  ProfileService,
} from "@/shared/api/services";
import { useAuth, useUI } from "@/shared/state/store";
import { calculateAge } from "@/shared/lib/utils/others";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { normaliseCount } from "@/shared/lib/utils/normaliseCount";
import ProfileFormDialog from "./profile/ProfileFormDialog";
import employmentStatusSchema from "@/engine/schema/income-onboarding/2-current-employment-status.json";
import dependentsDetailsSchema from "@/engine/schema/income-onboarding/4-details-for-dependents.json";
import vehicleDetailsSchema from "@/engine/schema/income-onboarding/5-vehicle-details.json";
import dateOfBirthSchema from "@/engine/schema/onboarding/6-date-of-birth.json";
import { useNavigate } from "react-router-dom";
import { ClearIncomeOptions } from "@/pages/private/fromDashboard/profilePage";
import { httpGet } from "@/shared/api/http";
import { customerEndpoints } from "@/shared/api/endpoints";
import femaCapture from "@/shared/fema/capture";
import type {
  CalculatedTotals,
  ExpenseUpdateRequest,
  ExpensesNoteUpdateRequest,
  IncomeNoteUpdateRequest,
  IncomeUpdateRequest,
  SmartReviewEntry,
} from "@/shared/api/types";
import { SmartReviewService } from "@/shared/api/services/smartReviewService";
import pencilGreenIcon from "@/assets/pencil-green.svg";
import OpenSectionIcon from "@/assets/open-section.svg";
import OpenSectionBlack from "@/assets/open-section-black.svg";

import {
  computeConfirmBeforeClearPlan,
  type BudgetSnapshots,
  type ProfileBudgetContext,
} from "@/shared/budget/confirmBeforeClear";
import { useUuFieldController } from "./uu/hooks/useUuFieldController";

interface ProfileField {
  label: string;
  value: string | number | boolean;
  editable?: boolean;
}

interface ProfileSection {
  title: string;
  fields: ProfileField[];
}

const Transition = React.forwardRef<
  unknown,
  { in?: boolean; children: React.ReactElement }
>(function Transition(props, ref) {
  return <Slide direction="up" ref={ref as React.Ref<unknown>} {...props} />;
});

interface SmartReviewDialogProps {
  open: boolean;
  onClose: () => void;
  incomeItems: any;
  fixedExpenseList: any;
  flexibleExpenseList: any;
  assetItems: any;
  handleEditItem: (arg0: string, arg1: string) => void;
  safeOpenEditDialog: any;
  setEditDialogConfig: any;
  nonPriorityDebts: any;
  priorityDebts: any;
  setTriggersCount: any;
}

interface smartReviewTrigger {
  id: string;
  condition: boolean | number;
  prompt: any;
  section: string;
  fieldName: string;
}

const SmartReviewDialog: React.FC<SmartReviewDialogProps> = ({
  setTriggersCount,
  open,
  onClose,
  incomeItems,
  assetItems,
  fixedExpenseList,
  flexibleExpenseList,
  handleEditItem,
  safeOpenEditDialog,
  setEditDialogConfig,
  nonPriorityDebts,
  priorityDebts,
}) => {
  const { user } = useAuth();
  const { addAlert, setLoading } = useUI();
  const navigate = useNavigate();

  const [confirmPendingAction, setConfirmPendingAction] = useState<
    null | (() => Promise<void>)
  >(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState<string>(
    "This change will remove some saved budget data. Do you want to continue?"
  );

  const uuController = useUuFieldController({ userId: user?.userId ?? null });

  // Helper: store an async callback in state without it being treated as a state-updater
  const setPendingConfirmAction = (action: () => Promise<void>): void => {
    setConfirmPendingAction(() => action);
  };

  const [summary, setSummary] = useState<CalculatedTotals>({
    customerId: 0,
    totalIncome: 0,
    totalAssets: 0,
    totalOutgoingsFixed: 0,
    totalOutgoingsFlexible: 0,
    totalOwingOnPriorityDebt: 0,
    totalRepayOnPriorityDebt: 0,
    totalOwingOnNonPriorityDebt: 0,
    totalRepayOnNonPriorityDebt: 0,
    pendingPaymentOffer: 0,
    balanceAmount: 0,
    totalRepaymentDebts: 0,
    totalOwingOnDebts: 0,
  });
  const [prefillData, setPrefillData] = useState<Record<string, unknown>>({});
  const [smartReviewTriggers, setSmartReviewTriggers] = useState<
    smartReviewTrigger[]
  >([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [marketingRecord, setMarketingRecord] = useState<{
    id?: number;
    lastUpdateDate?: string;
  } | null>(null);
  const [customerData, setCustomerData] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [employmentDialogOpen, setEmploymentDialogOpen] = useState(false);
  const [dobDialogOpen, setDobDialogOpen] = useState(false);
  const [dependencyToggle, setDependencyToggle] = useState(false);
  const [dependentsDialogOpen, setDependentsDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);

  const [dependentsInitialData, setDependentsInitialData] = useState<Record<
    string,
    unknown
  > | null>(null);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [savingFormId, setSavingFormId] = useState<string | null>(null);
  const [smartReviewMappings, setSmartReviewMappings] = useState<
    Record<string, unknown>
  >({
    id: 0,
    valProfileAgeEmploymentMismatch: "N",
    valProfileBenefitAgeMismatch: "N",
    valDependantsBenefitMismatch: "N",
    valIncomeUnemployedSalary: "N",
    valAccommodationParentCosts: "N",
    valVehicleCostsNoVehicle: "N",
    valVehicleNoCosts: "N",
    valSavingsTooHigh: "N",
    valDebtAmountsMissing: "N",
    valIncomeSmartReviewDate: "N",
  });

  const getString = (
    obj: Record<string, unknown> | null | undefined,
    key: string
  ): string => {
    if (!obj) return "";
    const v = obj[key];
    return typeof v === "string" ? v : "";
  };

  const beginSave = (formId: string) => {
    setSavingFormId(formId);
    setLoading("smart-review-save", true);
  };

  const endSave = () => {
    setLoading("smart-review-save", false);
    setSavingFormId(null);
  };

  const reportSaveError = (params: {
    message: string;
    error: unknown;
    formId?: string;
  }) => {
    addAlert({
      severity: "error",
      message: params.message,
      autoHide: true,
      duration: 2000,
    });
    try {
      femaCapture("smart_review_dialog_save_error", params.error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });
    } catch {
      // non-blocking
    }
  };

  const constructPrefillFromCustomer = (
    customer: Record<string, unknown>
  ): Record<string, unknown> => {
    const prefill: Record<string, unknown> = {};

    // Step 0: Referral method
    const heardAboutUs = getString(customer, "heardAboutUs");
    const textField2 = getString(customer, "textField2");
    const isCreditorReferral = textField2.toLowerCase() === "creditor";

    if (heardAboutUs) {
      prefill["onboarding-step-0"] = { referedFrom: heardAboutUs };
    } else if (isCreditorReferral) {
      // For creditor referrals, set referral method even if heardAboutUs is empty
      // This ensures the dialog opens with the correct radio button pre-selected
      prefill["onboarding-step-0"] = { referedFrom: "Referred by a company" };
    }

    // Step 1: Company referral details
    const field5TxtFld = getString(customer, "field5TxtFld");
    if (field5TxtFld) {
      const step1: Record<string, unknown> = {};

      if (field5TxtFld === "I have not been referred by a company") {
        step1.notReferredByCompany = true;
      } else {
        step1.referedFromCompany = field5TxtFld;

        // Do not derive product name from field3TxtFld (reserved for vulnerability audience)

        // Account type from textField3
        const textField3 = getString(customer, "textField3");
        if (textField3) {
          step1.accountType = textField3;
        }
      }

      prefill["onboarding-step-1"] = step1;
    }

    return prefill;
  };

  const coerceCustomerId = (
    customer: Record<string, unknown> | null
  ): number | null => {
    if (!customer) return null;
    const raw = customer["customerId"] ?? customer["id"];
    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
          ? Number(raw)
          : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  async function getSummary(customerId: number): Promise<void> {
    try {
      const response = await httpGet<ApiResponse<CalculatedTotals[]>>(
        customerEndpoints.getCalculatedTotals(customerId)
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        setSummary(response.data[0]);
      }
    } catch (error) {
      femaCapture("fetching_calculated_totals_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }

  const refreshProfile = async (options?: {
    showErrorAlert?: boolean;
  }): Promise<void> => {
    if (!user?.userId) return;
    try {
      // Ensure we don't read stale cached data after dialog saves
      try {
        OnboardingService.clearCustomerCache(user.userId);
      } catch {}
      const [prefillResp, customerResp] = await Promise.all([
        OnboardingService.loadCustomerPrefillData(user.userId, {
          skipErrorCapture: true,
        }),
        OnboardingService.getCustomerByUserId(user.userId, {
          skipErrorCapture: true,
        }),
      ]);

      // API returns an array with a single customer object - extract it
      let customerDataValue: Record<string, unknown> | null = null;
      if (customerResp?.data) {
        if (Array.isArray(customerResp.data) && customerResp.data.length > 0) {
          customerDataValue = customerResp.data[0] as Record<string, unknown>;
        } else if (typeof customerResp.data === "object") {
          customerDataValue = customerResp.data as Record<string, unknown>;
        }
      }
      setCustomerData(customerDataValue);

      const customerId = coerceCustomerId(customerDataValue);
      if (customerId !== null) {
        await getSummary(customerId);
      }

      // Load marketing preference
      try {
        if (customerId !== null) {
          const m = await MarketingService.get(customerId, {
            skipErrorCapture: true,
          });
          const rec = (m as any)?.data as {
            id?: number;
            lastUpdateDate?: string;
            optedOut?: "Y" | "N";
          } | null;

          // IE Hub backend semantics:
          // - No record (GET returns [])           => customer is OPTED IN
          // - Record exists with optedOut === 'Y'  => customer is OPTED OUT
          // - Record exists with optedOut === 'N'  => customer is OPTED IN
          if (rec && typeof rec.id === "number") {
            setMarketingRecord({
              id: rec.id,
              ...(rec.lastUpdateDate && { lastUpdateDate: rec.lastUpdateDate }),
            });
          } else {
            setMarketingRecord(null);
          }

          const isOptedOut = (rec?.optedOut ?? "N") === "Y";
          setMarketingConsent(!isOptedOut);
        }
      } catch {}

      // Start with prefill data from API if available
      let finalPrefillData: Record<string, unknown> = {};
      if (prefillResp && prefillResp.success && prefillResp.data) {
        finalPrefillData = { ...(prefillResp.data as Record<string, unknown>) };
      }

      // Always merge/augment with customer data for company referral fields
      // This ensures onboarding-step-0 and onboarding-step-1 are populated even if prefill API doesn't have them
      if (customerDataValue) {
        const constructedPrefill =
          constructPrefillFromCustomer(customerDataValue);

        // Merge step-0 (referral method) preferring API prefill over constructed
        if (constructedPrefill["onboarding-step-0"]) {
          finalPrefillData["onboarding-step-0"] = {
            ...((constructedPrefill["onboarding-step-0"] as Record<
              string,
              unknown
            >) || {}),
            ...((finalPrefillData["onboarding-step-0"] as Record<
              string,
              unknown
            >) || {}),
          };
        }

        // Merge step-1 (company details) preferring API prefill over constructed
        if (constructedPrefill["onboarding-step-1"]) {
          finalPrefillData["onboarding-step-1"] = {
            ...((constructedPrefill["onboarding-step-1"] as Record<
              string,
              unknown
            >) || {}),
            ...((finalPrefillData["onboarding-step-1"] as Record<
              string,
              unknown
            >) || {}),
          };
        }
      }

      setPrefillData(finalPrefillData);
    } catch (error) {
      if (options?.showErrorAlert) {
        addAlert({
          severity: "error",
          message: "Failed to refresh profile data",
          autoHide: true,
          duration: 2000,
        });
      }
      try {
        femaCapture("smart_review_dialog_refresh_profile_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
      } catch {
        // non-blocking
      }
    } finally {
      setIsLoaded(true);
    }
  };

  const getIncomeOnboardingStepData = (
    index: number
  ): Record<string, unknown> => {
    const key = `income-onboarding-step-${index}`;
    const val = prefillData?.[key] as Record<string, unknown> | undefined;
    return val || {};
  };

  const getStepData = (index: number): Record<string, unknown> => {
    const key = `onboarding-step-${index}`;
    const val = prefillData?.[key] as Record<string, unknown> | undefined;
    return val || {};
  };

  const truncateWithEllipsis = (value: string, maxLength = 20): string => {
    if (!value) return value;
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const composeName = (
    pd: Record<string, unknown>,
    customer: Record<string, unknown> | null
  ): string => {
    // Try to get title
    const title =
      getString(pd, "title") || getString(customer, "customerTitle");
    const titleCapitalized = title
      ? title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()
      : "";

    // Try to get first and last name from prefill data - BOTH must exist
    const first = getString(pd, "firstName");
    const last = getString(pd, "lastName");
    if (first && last) {
      const truncatedFirst = truncateWithEllipsis(first, 20);
      const truncatedLast = truncateWithEllipsis(last, 20);
      const nameParts = [
        titleCapitalized,
        truncatedFirst,
        truncatedLast,
      ].filter(Boolean);
      return nameParts.join(" ").trim();
    }

    // Try from customer data - check for separate first and last name fields (PRIORITY) - BOTH must exist
    const debtorFirst = getString(customer, "debtor1FirstName");
    const debtorLast = getString(customer, "debtor1LastName");

    if (debtorFirst && debtorLast) {
      const truncatedFirst = truncateWithEllipsis(debtorFirst, 20);
      const truncatedLast = truncateWithEllipsis(debtorLast, 20);
      const nameParts = [
        titleCapitalized,
        truncatedFirst,
        truncatedLast,
      ].filter(Boolean);
      return nameParts.join(" ").trim();
    }

    // Try debtor1Name which might contain full name (fallback)
    const debtorName = getString(customer, "debtor1Name");
    if (debtorName) {
      return titleCapitalized
        ? `${titleCapitalized} ${debtorName}`
        : debtorName;
    }

    const displayName = getString(customer, "displayName");
    if (displayName) {
      return titleCapitalized
        ? `${titleCapitalized} ${displayName}`
        : displayName;
    }

    return "";
  };

  const composeDob = (
    dobData: Record<string, unknown>,
    customer: Record<string, unknown> | null
  ): string => {
    const direct = getString(dobData, "dateOfBirth");
    if (direct) return formatDate(direct);

    const day = getString(dobData, "dobDay");
    const month = getString(dobData, "dobMonth");
    const year = getString(dobData, "dobYear");
    if (day && month && year) {
      const monthNum = Number(month);
      const monthIndex = Number.isFinite(monthNum)
        ? monthNum - 1
        : new Date(`${month} 1, 2000`).getMonth();
      if (Number.isFinite(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
        const dt = new Date(Number(year), monthIndex, Number(day));
        if (!Number.isNaN(dt.getTime())) {
          const dd = String(dt.getDate()).padStart(2, "0");
          const mm = String(dt.getMonth() + 1).padStart(2, "0");
          return `${dd}/${mm}/${dt.getFullYear()}`;
        }
      }
    }
    const compact = [day, month, year].filter(Boolean).join(" ");
    if (compact) return compact;

    // Try from customer data - debtor1DateOfBirthStr (e.g. "2001-09-29")
    const dobStr = customer && getString(customer, "debtor1DateOfBirthStr");
    if (dobStr) return formatDate(dobStr);

    // Try from customer data - debtor1DateOfBirth array format [2001, 9, 29]
    const dobArray = customer && customer["debtor1DateOfBirth"];
    if (Array.isArray(dobArray) && dobArray.length === 3) {
      const [year, month, day] = dobArray;
      const dd = String(day).padStart(2, "0");
      const mm = String(month).padStart(2, "0");
      return `${dd}/${mm}/${year}`;
    }

    return "";
  };

  const composeAddress = (addr: Record<string, unknown>): string => {
    const rawBuilding =
      getString(addr, "buildingNumber") || getString(addr, "addressLine1");
    const building = rawBuilding ? truncateWithEllipsis(rawBuilding, 20) : "";

    const parts = [
      building,
      getString(addr, "addressLine2"),
      getString(addr, "postTown") ||
        getString(addr, "city") ||
        getString(addr, "town"),
      getString(addr, "postcode") || getString(addr, "postCode"),
    ]
      .map((s) => s && String(s).trim())
      .filter(Boolean) as string[];
    return parts.join(", ");
  };

  const personalDetails: ProfileSection = useMemo(() => {
    const pd = getStepData(3);
    const dob = getStepData(4);
    const contact = getStepData(5);
    const address = getStepData(6);

    const name = composeName(pd, customerData);
    const dateOfBirth = composeDob(dob, customerData);
    const email =
      getString(contact, "emailId") || getString(customerData, "emailAddress");
    const phone =
      getString(contact, "phoneNumber") ||
      getString(customerData, "phoneNumber");
    const landline =
      getString(contact, "landlineNumber") ||
      getString(customerData, "landlineNumber");

    // Compose address from API data if not in prefill
    let addressStr = composeAddress(address);
    if (!addressStr || addressStr === "-") {
      const buildingNumber = getString(customerData, "buildingNumber");
      const postCode = getString(customerData, "postCode");
      const parts = [buildingNumber, postCode].filter(Boolean);
      addressStr = parts.join(", ");
    }

    // Accommodation comes from income-onboarding-step-0
    const incomeStep0 = getIncomeOnboardingStepData(0);
    const accommodation =
      getString(incomeStep0, "accomodationType") ||
      getString(customerData, "housingTenure") ||
      "";

    const incomeStep1 = getIncomeOnboardingStepData(1);
    const employmentStatus =
      getString(incomeStep1, "customerEmploymentStatus") ||
      getString(customerData, "customerEmploymentStatus") ||
      "";

    return {
      title: "Personal and contact details",
      fields: [
        { label: "Name", value: name || "-", editable: true },
        { label: "Date of birth", value: dateOfBirth || "-", editable: true },
        { label: "Email", value: email || "-", editable: false },
        { label: "Phone number", value: phone || "-", editable: true },
        { label: "Address", value: addressStr || "-", editable: true },
        { label: "Landline number", value: landline || "-", editable: true },
        {
          label: "Employment status",
          value: employmentStatus || "-",
          editable: true,
        },
        {
          label: "Accommodation Type",
          value: accommodation || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const vehicles = useMemo(() => {
    // Vehicles comes from income-onboarding-step-4
    const incomeStep4 = getIncomeOnboardingStepData(4);
    // Robust haveVehicle parsing
    const rawHaveVehicle = incomeStep4["haveVehicle"];
    let haveVehicle: boolean | undefined = undefined;
    if (typeof rawHaveVehicle === "boolean") {
      haveVehicle = rawHaveVehicle;
    } else if (typeof rawHaveVehicle === "string") {
      const hv = rawHaveVehicle.trim().toLowerCase();
      haveVehicle = hv === "y" || hv === "yes" || hv === "true" || hv === "1";
    } else {
      const apiValue = getString(customerData, "haveVehicle");
      haveVehicle = apiValue === "Y";
    }

    // Robust vehicle count parsing
    const rawNum = incomeStep4["noOfVehiclesInHouseholds"];
    let numVehicles: number | undefined = undefined;
    if (typeof rawNum === "number") {
      numVehicles = rawNum;
    } else if (typeof rawNum === "string") {
      const parsed = parseInt(rawNum, 10);
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    } else if (typeof customerData?.["noOfVehiclesInHouseholds"] === "number") {
      numVehicles = customerData["noOfVehiclesInHouseholds"] as number;
    } else if (typeof customerData?.["noOfVehiclesInHouseholds"] === "string") {
      const parsed = parseInt(
        customerData["noOfVehiclesInHouseholds"] as unknown as string,
        10
      );
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    } else if (typeof customerData?.["vehicles"] === "number") {
      numVehicles = customerData["vehicles"] as number;
    } else if (typeof customerData?.["vehicles"] === "string") {
      const parsed = parseInt(
        customerData["vehicles"] as unknown as string,
        10
      );
      if (!Number.isNaN(parsed)) numVehicles = parsed;
    }

    // Clamp to [0, 5]
    if (typeof numVehicles === "number") {
      numVehicles = Math.min(Math.max(numVehicles, 0), 5);
    }

    const vehicleCount = ((): string => {
      if (typeof haveVehicle === "boolean") {
        if (!haveVehicle) return "0";
        if (typeof numVehicles === "number") return String(numVehicles);
      }
      if (typeof numVehicles === "number") return String(numVehicles);
      return "";
    })();
    return {
      title: "Vehicles",
      count: vehicleCount || "-",
    };
  }, [prefillData, customerData]);

  const propertyAndPossessions: ProfileSection = useMemo(() => {
    const incomeStep0 = getIncomeOnboardingStepData(0);
    const accommodation =
      getString(incomeStep0, "accomodationType") ||
      getString(customerData, "housingTenure") ||
      "";
    return {
      title: "Property and possessions",
      fields: [
        {
          label: "Number of Vehicle owned",
          value: vehicles.count || "-",
          editable: true,
        },
        {
          label: "Accommodation Type",
          value: accommodation || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const resolveRecordIds = async (): Promise<{
    recordId: number | null;
    customerId: number | null;
  }> => {
    try {
      const ls = localStorage.getItem("customerData");
      if (ls) {
        const obj = JSON.parse(ls) as Record<string, unknown>;
        const rid =
          typeof obj["id"] === "number"
            ? (obj["id"] as number)
            : typeof obj["customerId"] === "number"
              ? (obj["customerId"] as number)
              : null;
        const cid =
          typeof obj["customerId"] === "number"
            ? (obj["customerId"] as number)
            : typeof obj["id"] === "number"
              ? (obj["id"] as number)
              : null;
        if (rid && cid) return { recordId: rid, customerId: cid };
      }
    } catch {
      // ignore parse
    }
    if (user?.userId) {
      try {
        const resp = await OnboardingService.getCustomerByUserId(user.userId, {
          skipErrorCapture: true,
        });
        const data = resp?.data as Record<string, unknown> | undefined;
        if (data) {
          const rid =
            typeof data["id"] === "number"
              ? (data["id"] as number)
              : typeof data["customerId"] === "number"
                ? (data["customerId"] as number)
                : null;
          const cid =
            typeof data["customerId"] === "number"
              ? (data["customerId"] as number)
              : typeof data["id"] === "number"
                ? (data["id"] as number)
                : null;
          return { recordId: rid, customerId: cid };
        }
      } catch {
        // ignore
      }
    }
    return { recordId: null, customerId: null };
  };

  const handleVehicleSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    try {
      beginSave("profile-vehicle-form");
      const ids = await resolveRecordIds();

      // Schema returns: haveVehicle (boolean), noOfVehiclesInHouseholds (number)
      const haveVehicle =
        typeof data["haveVehicle"] === "boolean"
          ? (data["haveVehicle"] as boolean)
          : false;
      const countRaw = data["noOfVehiclesInHouseholds"];
      const count =
        typeof countRaw === "number"
          ? countRaw
          : typeof countRaw === "string"
            ? parseInt(countRaw as string, 10)
            : 0;
      // Default to 1 when switching to Yes with empty/zero, clamp to max 5
      const vehiclesNum = haveVehicle
        ? Number.isFinite(count) && count > 0
          ? Math.min(count, 5)
          : 1
        : 0;

      // Update income-onboarding-step-4 for prefill
      const merged = {
        ...prefillData,
        ["income-onboarding-step-4"]: {
          haveVehicle: haveVehicle,
          noOfVehiclesInHouseholds: vehiclesNum,
        },
      };
      await OnboardingService.updateCustomer(
        merged,
        user.userId,
        (ids.recordId as number) || (ids.customerId as number),
        (ids.customerId as number) || (ids.recordId as number),
        (user.emailAddress as string) || "",
        { skipErrorCapture: false }
      );

      // Update actual customer data via ProfileService
      if (ids.customerId) {
        await ProfileService.patchCustomerData(ids.customerId, {
          haveVehicle: haveVehicle ? "Y" : "N",
          noOfVehiclesInHouseholds: vehiclesNum,
        });
      }

      setVehicleDialogOpen(false);
      await refreshProfile({ showErrorAlert: true });
    } catch (error) {
      reportSaveError({
        message: "Failed to save vehicle details",
        error,
        formId: "profile-vehicle-form",
      });
    } finally {
      endSave();
    }
  };

  const handleEmploymentSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    // Schema returns 'customerEmploymentStatus'
    const selected =
      typeof data["customerEmploymentStatus"] === "string"
        ? (data["customerEmploymentStatus"] as string)
        : "";

    // Wrapper for the actual save routine (used directly or after confirmation)
    const doSave = async () => {
      try {
        beginSave("profile-employment-form");
        const ids = await resolveRecordIds();
        // Update income-onboarding-step-1 for prefill
        const merged = {
          ...prefillData,
          ["income-onboarding-step-1"]: { customerEmploymentStatus: selected },
        };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          await ProfileService.patchCustomerData(ids.customerId, {
            customerEmploymentStatus: selected,
          });
        }

        setEmploymentDialogOpen(false);
        await refreshProfile({ showErrorAlert: true });
        setDependencyToggle(!dependencyToggle);
      } catch (error) {
        reportSaveError({
          message: "Failed to save employment status",
          error,
          formId: "profile-employment-form",
        });
      } finally {
        endSave();
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (customerId) {
        const snaps = await fetchBudgetSnapshotsFresh(customerId);
        const before = buildProfileBudgetContext();
        const after = buildProfileBudgetContext({
          customerEmploymentStatus: selected,
        });

        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId: Number(user.userId),
        });

        if (plan.needsConfirm) {
          setConfirmMessage(
            `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`
          );
          const cid = Number(customerId);
          const uid = Number(user.userId);
          const { income, pension, expense } =
            buildClearOptionsFromConfirmPlan(plan);

          setPendingConfirmAction(async () => {
            if (hasAnyIncomeClear(income)) {
              await clearHiddenIncomeFields(cid, { ...income, userId: uid });
            }
            if (hasAnyPensionClear(pension)) {
              await clearHiddenPensionFields(cid, { ...pension, userId: uid });
            }
            if (hasAnyExpenseClear(expense)) {
              await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
            }
            await doSave();
          });
          setConfirmOpen(true);
          return;
        }
      }
    } catch {
      // fall back to saving
    }

    await doSave();
  };

  const handleDobSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    const doSave = async () => {
      try {
        beginSave("profile-dob-form");
        const ids = await resolveRecordIds();
        const merged = { ...prefillData, ["onboarding-step-4"]: data };
        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );
        setDobDialogOpen(false);
        await refreshProfile({ showErrorAlert: true });
        setDependencyToggle(!dependencyToggle);
      } catch (error) {
        reportSaveError({
          message: "Failed to save date of birth",
          error,
          formId: "profile-dob-form",
        });
      } finally {
        endSave();
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (!customerId) {
        await doSave();
        return;
      }

      const newDob = data["dateOfBirth"];
      const newDobStr =
        newDob instanceof Date ? formatDateToYyyyMmDd(newDob) : null;
      const customerAgeAfter =
        typeof newDobStr === "string" ? computeAgeSafe(newDobStr) : undefined;

      if (typeof customerAgeAfter !== "number") {
        await doSave();
        return;
      }

      const snaps = await fetchBudgetSnapshotsFresh(customerId);
      const before = buildProfileBudgetContext();
      const after = buildProfileBudgetContext({
        customerAge: customerAgeAfter,
      });

      const plan = computeConfirmBeforeClearPlan({
        before,
        after,
        snaps,
        userId: Number(user.userId),
      });

      if (plan.needsConfirm) {
        setConfirmMessage(
          `This change will remove the data you entered for: ${plan.clearedLabels.join(
            ", "
          )}. Do you want to continue?`
        );
        const cid = Number(customerId);
        const uid = Number(user.userId);
        const { income, pension, expense } =
          buildClearOptionsFromConfirmPlan(plan);

        setPendingConfirmAction(async () => {
          if (hasAnyIncomeClear(income)) {
            await clearHiddenIncomeFields(cid, { ...income, userId: uid });
          }
          if (hasAnyPensionClear(pension)) {
            await clearHiddenPensionFields(cid, { ...pension, userId: uid });
          }
          if (hasAnyExpenseClear(expense)) {
            await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
          }
          await doSave();
        });
        setConfirmOpen(true);
        return;
      }
    } catch {
      // fall back to saving
    }

    await doSave();
  };

  const childrenAndAdults = useMemo(() => {
    // Children & Adults comes from income-onboarding-step-3
    const incomeStep3 = getIncomeOnboardingStepData(3);
    const hasDependents = ((): boolean => {
      if (typeof incomeStep3["haveDependents"] === "boolean")
        return incomeStep3["haveDependents"] as boolean;
      
      // Check if there are children under 18
      const apiValue = getString(customerData, "haveChildrenUnder18");
      if (apiValue === "Y") return true;
      
      // Also check if there are other adults over 18
      // adultsOver18IncludingMyself is actually other adults excluding customer and partner
      const otherAdultsRaw = customerData?.["adultsOver18IncludingMyself"];
      
      if (typeof otherAdultsRaw === "number" && otherAdultsRaw > 0) {
        return true;
      }
      
      return false;
    })();
    if (!hasDependents) {
      return {
        title: "Household",
        fields: [
          { label: "Dependants under 16", value: "No child", editable: true },
          {
            label: "Dependants between 16 - 18",
            value: "No child",
            editable: true,
          },
          {
            label: "Dependants over 18",
            value: "No other adults",
            editable: true,
          },
        ],
      } as ProfileSection;
    }

    const dependantsUnder16 =
      typeof incomeStep3["under16Dependents"] === "number"
        ? String(incomeStep3["under16Dependents"])
        : typeof customerData?.["dependentChildrenUnder16"] === "number"
          ? String(customerData["dependentChildrenUnder16"])
          : "";

    const dependants16to18 =
      typeof incomeStep3["between16to18Dependents"] === "number"
        ? String(incomeStep3["between16to18Dependents"])
        : typeof customerData?.["dependentChildrenAged16Plus"] === "number"
          ? String(customerData["dependentChildrenAged16Plus"])
          : "";

    const dependantsOver18 = ((): string => {
      if (typeof incomeStep3["otherAdultsOver18"] === "number") {
        return String(incomeStep3["otherAdultsOver18"]);
      }
      if (typeof customerData?.["adultsOver18IncludingMyself"] === "number") {
        // adultsOver18IncludingMyself is actually other adults excluding customer and partner
        return String(customerData["adultsOver18IncludingMyself"]);
      }
      return "";
    })();

    return {
      title: "Household",
      fields: [
        {
          label: "Dependants under 16",
          value: dependantsUnder16 || "-",
          editable: true,
        },
        {
          label: "Dependants between 16 - 18",
          value: dependants16to18 || "-",
          editable: true,
        },
        {
          label: "Dependants over 18",
          value: dependantsOver18 || "-",
          editable: true,
        },
      ],
    };
  }, [prefillData, customerData]);

  const handleDependentsDialogOpen = async () => {
    try {
      // Data comes from income-onboarding-step-3
      const incomeStep3 = getIncomeOnboardingStepData(3);
      // Also read partner completion from income-onboarding-step-2/customer API
      const incomeStep2 = getIncomeOnboardingStepData(2);

      // Helper to safely coerce numeric-looking values to numbers
      const coerceToNumber = (value: unknown, fallback: number = 0): number => {
        if (typeof value === "number" && Number.isFinite(value)) return value;
        if (typeof value === "string") {
          const parsed = parseInt(value, 10);
          return Number.isNaN(parsed) ? fallback : parsed;
        }
        return fallback;
      };

      // counts (ensure numbers, not strings)
      const under16 =
        typeof incomeStep3["under16Dependents"] === "number"
          ? incomeStep3["under16Dependents"]
          : coerceToNumber(customerData?.["dependentChildrenUnder16"]);

      const between16to18 =
        typeof incomeStep3["between16to18Dependents"] === "number"
          ? incomeStep3["between16to18Dependents"]
          : coerceToNumber(customerData?.["dependentChildrenAged16Plus"]);

      // Calculate other adults first (need this for haveDependents check)
      // adultsOver18IncludingMyself is actually other adults excluding customer and partner
      const otherAdults = ((): number => {
        if (typeof incomeStep3["otherAdultsOver18"] === "number") {
          return incomeStep3["otherAdultsOver18"];
        }
        return coerceToNumber(customerData?.["adultsOver18IncludingMyself"]);
      })();

      // haveDependents boolean - check step data, then children, then other adults
      const raw = incomeStep3["haveDependents"];
      let haveDependents = typeof raw === "boolean" ? raw : false;
      if (!haveDependents) {
        const apiValue = getString(customerData, "haveChildrenUnder18");
        if (apiValue === "Y") {
          haveDependents = true;
        } else if (under16 > 0 || between16to18 > 0 || otherAdults > 0) {
          // Also check if there are any dependents or other adults
          haveDependents = true;
        }
      }

      // Determine with-partner flag from either step-2 or API 'jointAccount' (Y/N)
      const withPartner = ((): boolean => {
        const raw = incomeStep2["budgetWithPartner"];
        if (typeof raw === "boolean") return raw;
        const joint = getString(customerData, "jointAccount");
        return joint === "Y";
      })();

      const step3Data: Record<string, unknown> = {
        haveDependents,
        under16Dependents: under16,
        between16to18Dependents: between16to18,
        otherAdultsOver18: otherAdults,
        // Provide context for count label expression
        budgetWithPartner: withPartner,
        jointAccount:
          getString(customerData, "jointAccount") || (withPartner ? "Y" : "N"),
      };

      if (haveDependents && customerData?.customerId) {
        // Fetch dependent DOBs from the API
        try {
          const dependentsResponse = await DependentsService.getDependents(
            customerData.customerId as number
          );
          if (dependentsResponse.success && dependentsResponse.data) {
            // Map DOBs to form fields based on category - with numbered fields
            const under16Dependents = dependentsResponse.data
              .filter((dep) => dep.dependantCategory === "age-under16")
              .sort(
                (a, b) =>
                  Number(a.childDependantNo) - Number(b.childDependantNo)
              );

            const between16to18Dependents = dependentsResponse.data
              .filter((dep) => dep.dependantCategory === "age-16to18")
              .sort(
                (a, b) =>
                  Number(a.childDependantNo) - Number(b.childDependantNo)
              );

            // Set numbered DOB fields for under16 dependents
            under16Dependents.forEach((dep) => {
              const dobStr = dep.childDependantDobStr;
              if (dobStr && typeof dobStr === "string") {
                const fieldName = `under16DependentsDOB_${dep.childDependantNo}`;
                step3Data[fieldName] = new Date(dobStr);
              }
            });

            // Set numbered DOB fields for 16-18 dependents
            between16to18Dependents.forEach((dep) => {
              const dobStr = dep.childDependantDobStr;
              if (dobStr && typeof dobStr === "string") {
                const fieldName = `between16to18DependentsDOB_${dep.childDependantNo}`;
                step3Data[fieldName] = new Date(dobStr);
              }
            });
          }
        } catch (error) {
          // Failed to fetch dependent DOBs
        }
      }

      setDependentsInitialData(step3Data);
      setDependentsDialogOpen(true);
    } catch (error) {
      // Failed to prepare dependents dialog
      setDependentsDialogOpen(true);
    }
  };

  const hasPositive = (value: unknown): boolean => {
    if (typeof value === "number") return Number.isFinite(value) && value > 0;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      const n = Number(cleaned);
      return Number.isFinite(n) && n > 0;
    }
    return false;
  };

  const hasNonEmptyText = (value: unknown): boolean => {
    return typeof value === "string" && value.trim().length > 0;
  };

  const formatDateToYyyyMmDd = (date: Date): string | null => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const computeAgeSafe = (dobStr: string | undefined): number | undefined => {
    if (!dobStr) return undefined;
    try {
      const age = calculateAge(dobStr);
      return typeof age === "number" && Number.isFinite(age) ? age : undefined;
    } catch {
      return undefined;
    }
  };

  interface ClearPensionOptions {
    clearStatePension?: boolean;
    clearPrivatePension?: boolean;
    clearPensionCredit?: boolean;
    clearPensionOthers?: boolean;
    userId?: number;
  }

  interface ClearExpenseOptions {
    clearSchoolCosts?: boolean;
    clearPocketMoney?: boolean;
    userId?: number;
  }

  const fetchBudgetSnapshotsFresh = async (
    customerId: number
  ): Promise<BudgetSnapshots> => {
    try {
      const [incomeResp, incomeNotesResp, expenseResp, expenseNotesResp] =
        await Promise.all([
          BudgetService.getIncomeData(customerId, { skipCache: true }),
          BudgetService.getIncomeNotes(customerId, { skipCache: true }),
          BudgetService.getExpenseData(customerId, { skipCache: true }),
          BudgetService.getExpenseNotes(customerId, { skipCache: true }),
        ]);

      const income =
        incomeResp?.success &&
        Array.isArray(incomeResp.data) &&
        incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const incomeNotes =
        incomeNotesResp?.success &&
        Array.isArray(incomeNotesResp.data) &&
        incomeNotesResp.data[0]
          ? incomeNotesResp.data[0]
          : null;
      const expenses =
        expenseResp?.success &&
        Array.isArray(expenseResp.data) &&
        expenseResp.data[0]
          ? expenseResp.data[0]
          : null;
      const expenseNotes =
        expenseNotesResp?.success &&
        Array.isArray(expenseNotesResp.data) &&
        expenseNotesResp.data[0]
          ? expenseNotesResp.data[0]
          : null;

      return { income, incomeNotes, expenses, expenseNotes };
    } catch {
      return {
        income: null,
        incomeNotes: null,
        expenses: null,
        expenseNotes: null,
      };
    }
  };

  const coerceNumberFromRecord = (
    obj: Record<string, unknown> | null | undefined,
    key: string
  ): number | undefined => {
    if (!obj) return undefined;
    const v = obj[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string") {
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? undefined : n;
    }
    return undefined;
  };

  const buildProfileBudgetContext = (
    overrides: Partial<ProfileBudgetContext> = {}
  ): ProfileBudgetContext => {
    const customerEmploymentStatus = getString(
      customerData,
      "customerEmploymentStatus"
    );
    const partnerEmploymentStatus = getString(
      customerData,
      "partnerEmploymentStatus"
    );
    const jointAccount = getString(customerData, "jointAccount");
    const accommodationType = getString(customerData, "housingTenure");

    const haveChildrenUnder18 = getString(customerData, "haveChildrenUnder18");
    const dependentChildrenUnder16 = coerceNumberFromRecord(
      customerData,
      "dependentChildrenUnder16"
    );
    const dependentChildrenAged16Plus = coerceNumberFromRecord(
      customerData,
      "dependentChildrenAged16Plus"
    );

    const customerDobBefore =
      getString(customerData, "debtor1DateOfBirthStr") ||
      getString(customerData, "dateOfBirth");
    const partnerDobBefore =
      getString(customerData, "debtor2DateOfBirthStr") ||
      getString(customerData, "debtor2DateOfBirth") ||
      getString(customerData, "partnerDateOfBirth");

    const customerAge = computeAgeSafe(customerDobBefore);
    const partnerAge = computeAgeSafe(partnerDobBefore);

    return {
      customerEmploymentStatus,
      partnerEmploymentStatus,
      jointAccount,
      accommodationType,
      haveChildrenUnder18,
      ...(typeof dependentChildrenUnder16 === "number" && {
        dependentChildrenUnder16,
      }),
      ...(typeof dependentChildrenAged16Plus === "number" && {
        dependentChildrenAged16Plus,
      }),
      ...(typeof customerAge === "number" && { customerAge }),
      ...(typeof partnerAge === "number" && { partnerAge }),
      ...overrides,
    };
  };

  const buildClearOptionsFromConfirmPlan = (
    plan: ReturnType<typeof computeConfirmBeforeClearPlan>
  ): {
    income: ClearIncomeOptions;
    pension: ClearPensionOptions;
    expense: ClearExpenseOptions;
  } => {
    const income: ClearIncomeOptions = {};
    const pension: ClearPensionOptions = {};
    const expense: ClearExpenseOptions = {};

    for (const f of plan.fields) {
      if (!(f.beforeVisible && !f.afterVisible && f.hasData)) continue;
      switch (f.id) {
        case "salary.takeHomeSalary":
          income.hideCustomerSalary = true;
          break;
        case "salary.partnerTakeHomeSalary":
          income.hidePartnerSalary = true;
          break;
        case "salary.otherSalarySource":
          income.hideOtherSalary = true;
          break;
        case "benefits.jobSeekerAllowanceContribute":
          income.clearJobSeekerAllowanceContribute = true;
          break;
        case "benefits.incomeSupport":
          income.clearIncomeSupport = true;
          break;
        case "benefits.carerAllowance":
          income.clearCarerAllowance = true;
          break;
        case "benefits.childBenefit":
          income.clearChildBenefit = true;
          break;
        case "benefits.housingBenefitLocalAllowance":
          income.clearHousingBenefit = true;
          break;
        case "otherSources.childMaintenanceOrSupport":
          income.clearChildMaintenance = true;
          break;
        case "pension.statePension":
          pension.clearStatePension = true;
          break;
        case "pension.pensionCredit":
          pension.clearPensionCredit = true;
          break;
        case "pension.privatePension":
          pension.clearPrivatePension = true;
          break;
        case "pension.pensionOthers":
          pension.clearPensionOthers = true;
          break;
        case "expenses.schoolUniform":
        case "expenses.clubsAndSchoolTrips":
        case "expenses.otherSchoolCosts":
          expense.clearSchoolCosts = true;
          break;
        case "expenses.pocketMoney":
          expense.clearPocketMoney = true;
          break;
        default:
          break;
      }
    }

    return { income, pension, expense };
  };

  const hasAnyIncomeClear = (opts: ClearIncomeOptions): boolean =>
    Boolean(
      opts.hideCustomerSalary ||
        opts.hidePartnerSalary ||
        opts.hideOtherSalary ||
        opts.clearChildBenefit ||
        opts.clearHousingBenefit ||
        opts.clearChildMaintenance ||
        opts.clearJobSeekerAllowanceContribute ||
        opts.clearIncomeSupport ||
        opts.clearCarerAllowance
    );

  const hasAnyPensionClear = (opts: ClearPensionOptions): boolean =>
    Boolean(
      opts.clearStatePension ||
        opts.clearPrivatePension ||
        opts.clearPensionCredit ||
        opts.clearPensionOthers
    );

  const hasAnyExpenseClear = (opts: ClearExpenseOptions): boolean =>
    Boolean(opts.clearSchoolCosts || opts.clearPocketMoney);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const computePensionEligibility = (params: {
    hasPartner: boolean;
    customerAge?: number;
    partnerAge?: number;
    customerEmploymentStatus?: string;
    partnerEmploymentStatus?: string;
  }): { anyPensionQuestions: boolean; anyPension65Questions: boolean } => {
    const hasPartner = params.hasPartner === true;
    const cAge = params.customerAge;
    const pAge = params.partnerAge;
    const cRetired = params.customerEmploymentStatus === "Retired";
    const pRetired = params.partnerEmploymentStatus === "Retired";

    const customerQualifies =
      (typeof cAge === "number" && cAge >= 40) || cRetired;
    const partnerQualifies =
      hasPartner && ((typeof pAge === "number" && pAge >= 40) || pRetired);

    const anyPensionQuestions = customerQualifies || partnerQualifies;
    const anyPension65Questions =
      (typeof cAge === "number" && cAge >= 65) ||
      (hasPartner && typeof pAge === "number" && pAge >= 65);

    return { anyPensionQuestions, anyPension65Questions };
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pensionGroupHasData = (params: {
    income: Record<string, unknown>;
    notes: Record<string, unknown> | null;
    amountKey: string;
    notesKey: string;
  }): boolean => {
    const { income, notes, amountKey, notesKey } = params;
    const hasAmount = hasPositive(income[amountKey]);
    const hasNotes = notes ? hasNonEmptyText(notes[notesKey]) : false;
    return hasAmount || hasNotes;
  };

  const clearHiddenPensionFields = async (
    customerId: number,
    opts: ClearPensionOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;
      if (
        !opts.clearStatePension &&
        !opts.clearPrivatePension &&
        !opts.clearPensionCredit &&
        !opts.clearPensionOthers
      ) {
        return;
      }

      const [incomeResp, notesResp] = await Promise.all([
        BudgetService.getIncomeData(customerId, { skipCache: true }),
        BudgetService.getIncomeNotes(customerId, { skipCache: true }),
      ]);

      const baseIncome =
        incomeResp?.success &&
        Array.isArray(incomeResp.data) &&
        incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      // Clear amounts (do not clear mode)
      if (baseIncome) {
        const nowIso = new Date().toISOString();
        const incomePayload: IncomeUpdateRequest = {
		 ...(baseIncome as unknown as Record<string, unknown>),	
          id: baseIncome.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
       } as IncomeUpdateRequest;
        if (opts.clearStatePension) incomePayload.statePension = 0;
        if (opts.clearPrivatePension) incomePayload.privatePension = 0;
        if (opts.clearPensionCredit) incomePayload.pensionCredit = 0;
        if (opts.clearPensionOthers) incomePayload.pensionOthers = 0;

        await BudgetService.updateIncomeData(incomePayload);
      }

      // Clear notes (notes are stored separately; do not touch mode)
      if (baseNotes) {
        const notesPayload: IncomeNoteUpdateRequest = {
			 ...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: new Date().toISOString(),
         } as IncomeNoteUpdateRequest;
        if (opts.clearStatePension) notesPayload.statePensionNotes = "";
        if (opts.clearPrivatePension) notesPayload.privatePensionNotes = "";
        if (opts.clearPensionCredit) notesPayload.pensionCreditNotes = "";
        if (opts.clearPensionOthers) notesPayload.pensionOthersNotes = "";

        await BudgetService.updateIncomeNotes(notesPayload);
      }
    } catch {
      // Non-blocking
    }
  };

  const clearHiddenIncomeFields = async (
    customerId: number,
    opts: ClearIncomeOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;

      if (
        !opts.hideCustomerSalary &&
        !opts.hidePartnerSalary &&
        !opts.hideOtherSalary &&
        !opts.clearChildBenefit &&
        !opts.clearHousingBenefit &&
        !opts.clearChildMaintenance &&
        !opts.clearJobSeekerAllowanceContribute &&
        !opts.clearIncomeSupport &&
        !opts.clearCarerAllowance
      ) {
        return;
      }

      const [incomeResp, notesResp] = await Promise.all([
        BudgetService.getIncomeData(customerId, { skipCache: true }),
        BudgetService.getIncomeNotes(customerId, { skipCache: true }),
      ]);

      const baseIncome =
        incomeResp?.success &&
        Array.isArray(incomeResp.data) &&
        incomeResp.data[0]
          ? incomeResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      if (!baseIncome) return;

      const nowIso = new Date().toISOString();
      const incomeUpdate: IncomeUpdateRequest = {
		    ...(baseIncome as unknown as Record<string, unknown>),
        id: baseIncome.id,
        customerId: Number(customerId),
        lastUpdatedBy: Number(userId),
        lastUpdateDate: nowIso,
        } as IncomeUpdateRequest;


      if (opts.hideCustomerSalary) {
        incomeUpdate.basicIncome = 0;
      }

      if (opts.hidePartnerSalary) {
        incomeUpdate.debtor2BasicIncome = 0;
      }

      if (opts.hideOtherSalary) {
        incomeUpdate.otherSalaryWages1 = 0;
      }

      if (opts.clearJobSeekerAllowanceContribute) {
        incomeUpdate.jobseekerAllowanceContribute = 0;
      }

      if (opts.clearIncomeSupport) {
        incomeUpdate.incomeSupport = 0;
      }

      if (opts.clearCarerAllowance) {
        incomeUpdate.carerAllowance = 0;
      }

      if (opts.clearChildBenefit) {
        incomeUpdate.childBenefit = 0;
      }

      if (opts.clearHousingBenefit) {
        incomeUpdate.housingBenefitLocalAllowance = 0;
      }

      if (opts.clearChildMaintenance) {
        incomeUpdate.maintenanceOrChildSupport = 0;
      }

      await BudgetService.updateIncomeData(incomeUpdate);

      if (baseNotes) {
        const notesUpdate: IncomeNoteUpdateRequest = {
			  ...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
           } as IncomeNoteUpdateRequest;
        if (opts.hideCustomerSalary) notesUpdate.basicIncomeNotes = "";
        if (opts.hidePartnerSalary) notesUpdate.debtor2BasicIncomeNotes = "";
        if (opts.hideOtherSalary) notesUpdate.otherSalaryWages1Notes = "";
        if (opts.clearJobSeekerAllowanceContribute)
          notesUpdate.jobseekerAllowanceContributeNotes = "";
        if (opts.clearIncomeSupport) notesUpdate.incomeSupportNotes = "";
        if (opts.clearCarerAllowance) notesUpdate.carerAllowanceNotes = "";
        if (opts.clearChildBenefit) notesUpdate.childBenefitNotes = "";
        if (opts.clearHousingBenefit)
          notesUpdate.housingBenefitLocalAllowanceNotes = "";
        if (opts.clearChildMaintenance)
          notesUpdate.maintenanceOrChildSupportNotes = "";

        await BudgetService.updateIncomeNotes(notesUpdate);
      }
    } catch {
      // Non-blocking: if clearing fails, proceed with profile update
    }
  };

  const clearHiddenExpenseFields = async (
    customerId: number,
    opts: ClearExpenseOptions
  ): Promise<void> => {
    try {
      const userId = typeof opts.userId === "number" ? opts.userId : null;
      if (!userId) return;
      if (!opts.clearSchoolCosts && !opts.clearPocketMoney) return;

      const [expenseResp, notesResp] = await Promise.all([
        BudgetService.getExpenseData(customerId, { skipCache: true }),
        BudgetService.getExpenseNotes(customerId, { skipCache: true }),
      ]);

      const baseExpense =
        expenseResp?.success &&
        Array.isArray(expenseResp.data) &&
        expenseResp.data[0]
          ? expenseResp.data[0]
          : null;
      const baseNotes =
        notesResp?.success && Array.isArray(notesResp.data) && notesResp.data[0]
          ? notesResp.data[0]
          : null;

      if (!baseExpense) return;

      const nowIso = new Date().toISOString();
      const expensePayload: ExpenseUpdateRequest = {
		  ...(baseExpense as unknown as Record<string, unknown>),
        id: baseExpense.id,
        customerId: Number(customerId),
        lastUpdatedBy: Number(userId),
        lastUpdateDate: nowIso,
        } as ExpenseUpdateRequest;

      if (opts.clearSchoolCosts) {
        expensePayload.schoolUniform = 0;
        expensePayload.clubsAndSchoolTrips = 0;
        expensePayload.otherSchoolCosts = 0;
      }
      if (opts.clearPocketMoney) {
        expensePayload.pocketMoneyAndSchoolTrips = 0;
      }

      await BudgetService.updateExpenseData(expensePayload);

      if (baseNotes) {
        const notesPayload: ExpensesNoteUpdateRequest = {
			...(baseNotes as unknown as Record<string, unknown>),
          id: baseNotes.id,
          customerId: Number(customerId),
          lastUpdatedBy: Number(userId),
          lastUpdateDate: nowIso,
         } as ExpensesNoteUpdateRequest;
        if (opts.clearSchoolCosts) {
          notesPayload.schoolUniformNotes = "";
          notesPayload.clubsAndSchoolTripsNotes = "";
          notesPayload.otherSchoolCostsNotes = "";
        }
        if (opts.clearPocketMoney) {
          notesPayload.pocketMoneyAndSchoolTripsNotes = "";
        }

        await BudgetService.updateExpenseNotes(notesPayload);
      }
    } catch {
      // Non-blocking
    }
  };

  const handleDependentsSave = async (data: Record<string, unknown>) => {
    if (!user?.userId) return;
    // Normalize payload for step-3: when haveDependents is false, force counts to 0 and strip DOBs
    const haveDeps = data?.haveDependents === true;
    const normalizedStep3: Record<string, unknown> = { ...data };
    if (!haveDeps) {
      normalizedStep3.under16Dependents = 0;
      normalizedStep3.between16to18Dependents = 0;
      normalizedStep3.otherAdultsOver18 = 0;
      // Remove any dynamically created DOB fields if present
      Object.keys(normalizedStep3).forEach((k) => {
        if (
          k.startsWith("under16DependentsDOB_") ||
          k.startsWith("between16to18DependentsDOB_")
        ) {
          delete normalizedStep3[k];
        }
      });
    }

    const doSave = async () => {
      try {
        beginSave("profile-dependents-form");
        const ids = await resolveRecordIds();

        // Update income-onboarding-step-3 for prefill
        const merged = {
          ...prefillData,
          ["income-onboarding-step-3"]: normalizedStep3,
        };

        await OnboardingService.updateCustomer(
          merged,
          user.userId,
          (ids.recordId as number) || (ids.customerId as number),
          (ids.customerId as number) || (ids.recordId as number),
          (user.emailAddress as string) || "",
          { skipErrorCapture: false }
        );

        // Update actual customer data via ProfileService
        if (ids.customerId) {
          const apiPayload: Record<string, unknown> = {};

          if (data.haveDependents !== undefined) {
            apiPayload.haveChildrenUnder18 =
              data.haveDependents === true ? "Y" : "N";

            if (data.haveDependents === true) {
              if (data.under16Dependents !== undefined) {
                apiPayload.dependentChildrenUnder16 = data.under16Dependents;
              }
              if (data.between16to18Dependents !== undefined) {
                apiPayload.dependentChildrenAged16Plus =
                  data.between16to18Dependents;
              }
              if (data.otherAdultsOver18 !== undefined) {
                // adultsOver18IncludingMyself is actually other adults excluding customer and partner
                apiPayload.adultsOver18IncludingMyself = data.otherAdultsOver18;
              }

              // Handle dependent DOBs - save to ie-Customer-Dependent-Dob API
              if (ids.customerId && user?.userId) {
                try {
                  // Get existing dependents to compare and update
                  const existingDependentsResponse =
                    await DependentsService.getDependents(ids.customerId);
                  const existingDependents =
                    existingDependentsResponse.success &&
                    existingDependentsResponse.data
                      ? existingDependentsResponse.data
                      : [];

                  // Process under16 DOBs - loop through numbered fields
                  const under16Count =
                    typeof data.under16Dependents === "number"
                      ? data.under16Dependents
                      : 0;
                  for (let i = 1; i <= under16Count; i += 1) {
                    const dobFieldName = `under16DependentsDOB_${i}`;
                    const dobValue = data[dobFieldName];

                    // Check if this DOB already exists for under16 category with this number
                    const existingUnder16 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-under16" &&
                        dep.childDependantNo === String(i)
                    );

                    // If cleared, delete existing record for this index
                    if (!dobValue) {
                      if (existingUnder16 && existingUnder16.id) {
                        try {
                          await DependentsService.deleteDependent(
                            ids.customerId as number,
                            existingUnder16.id
                          );
                        } catch {
                          // non-blocking
                        }
                      }
                      continue;
                    }

                    let dobDate: Date | null = null;

                    if (dobValue instanceof Date) {
                      dobDate = dobValue;
                    } else if (
                      typeof dobValue === "string" ||
                      typeof dobValue === "number"
                    ) {
                      dobDate = new Date(dobValue);
                    }

                    if (
                      dobDate instanceof Date &&
                      !Number.isNaN(dobDate.getTime())
                    ) {
                      const year = dobDate.getFullYear();
                      const month = String(dobDate.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const day = String(dobDate.getDate()).padStart(2, "0");
                      const dobStr = `${year}-${month}-${day}`;

                      if (existingUnder16 && existingUnder16.id) {
                        // Update existing record
                        const updatePayload = {
                          id: existingUnder16.id,
                          customerId: existingUnder16.customerId,
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-under16" as const,
                          lastUpdatedBy: Number(user.userId),
                          lastUpdateDate: new Date().toISOString(),
                        };
                        await DependentsService.updateDependent(updatePayload);
                      } else {
                        // Create new record
                        await DependentsService.createDependent({
                          customerId: Number(ids.customerId),
                          userId: Number(user.userId),
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-under16",
                          createdBy: Number(user.userId),
                        });
                      }
                    }
                  }

                  // Delete under16 dependents that exceed the current count
                  const deletableUnder16 = existingDependents.filter(
                    (dep) =>
                      dep.dependantCategory === "age-under16" &&
                      Number(dep.childDependantNo) > under16Count
                  );
                  for (const dep of deletableUnder16) {
                    if (dep.id) {
                      await DependentsService.deleteDependent(
                        ids.customerId as number,
                        dep.id
                      );
                    }
                  }

                  // Process 16-18 DOBs - loop through numbered fields
                  const between16to18Count =
                    typeof data.between16to18Dependents === "number"
                      ? data.between16to18Dependents
                      : 0;
                  for (let i = 1; i <= between16to18Count; i += 1) {
                    const dobFieldName = `between16to18DependentsDOB_${i}`;
                    const dobValue = data[dobFieldName];

                    // Check if this DOB already exists for 16-18 category with this number
                    const existing16to18 = existingDependents.find(
                      (dep) =>
                        dep.dependantCategory === "age-16to18" &&
                        dep.childDependantNo === String(i)
                    );

                    // If cleared, delete existing record for this index
                    if (!dobValue) {
                      if (existing16to18 && existing16to18.id) {
                        try {
                          await DependentsService.deleteDependent(
                            ids.customerId as number,
                            existing16to18.id
                          );
                        } catch {
                          // non-blocking
                        }
                      }
                      continue;
                    }

                    let dobDate: Date | null = null;

                    if (dobValue instanceof Date) {
                      dobDate = dobValue;
                    } else if (
                      typeof dobValue === "string" ||
                      typeof dobValue === "number"
                    ) {
                      dobDate = new Date(dobValue);
                    }

                    if (
                      dobDate instanceof Date &&
                      !Number.isNaN(dobDate.getTime())
                    ) {
                      const year = dobDate.getFullYear();
                      const month = String(dobDate.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const day = String(dobDate.getDate()).padStart(2, "0");
                      const dobStr = `${year}-${month}-${day}`;

                      if (existing16to18 && existing16to18.id) {
                        // Update existing record
                        const updatePayload = {
                          id: existing16to18.id,
                          customerId: existing16to18.customerId,
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-16to18" as const,
                          lastUpdatedBy: Number(user.userId),
                          lastUpdateDate: new Date().toISOString(),
                        };
                        await DependentsService.updateDependent(updatePayload);
                      } else {
                        // Create new record
                        await DependentsService.createDependent({
                          customerId: Number(ids.customerId),
                          userId: Number(user.userId),
                          childDependantDob: dobStr,
                          childDependantDobStr: dobStr,
                          childDependantNo: String(i),
                          dependantCategory: "age-16to18",
                          createdBy: Number(user.userId),
                        });
                      }
                    }
                  }

                  // Delete 16-18 dependents that exceed the current count
                  const deletable16to18 = existingDependents.filter(
                    (dep) =>
                      dep.dependantCategory === "age-16to18" &&
                      Number(dep.childDependantNo) > between16to18Count
                  );
                  for (const dep of deletable16to18) {
                    if (dep.id) {
                      await DependentsService.deleteDependent(
                        ids.customerId as number,
                        dep.id
                      );
                    }
                  }
                } catch {
                  // Failed to save dependent DOBs
                  // Don't throw - allow the rest of the form to save
                }
              }
            } else {
              // When switching to No, clear counts and delete all dependent DOB records
              apiPayload.dependentChildrenUnder16 = 0;
              apiPayload.dependentChildrenAged16Plus = 0;
              apiPayload.adultsOver18IncludingMyself = 0;
              try {
                const existingDependentsResponse =
                  await DependentsService.getDependents(ids.customerId);
                const existingDependents =
                  existingDependentsResponse.success &&
                  existingDependentsResponse.data
                    ? existingDependentsResponse.data
                    : [];
                for (const dep of existingDependents) {
                  if (dep.id) {
                    await DependentsService.deleteDependent(
                      ids.customerId as number,
                      dep.id
                    );
                  }
                }
              } catch {
                // Non-blocking
              }
            }
          }

          await ProfileService.patchCustomerData(ids.customerId, apiPayload);
        }

        setDependentsDialogOpen(false);
        await refreshProfile({ showErrorAlert: true });
      } catch (error) {
        reportSaveError({
          message: "Failed to save dependents details",
          error,
          formId: "profile-dependents-form",
        });
      } finally {
        endSave();
      }
    };

    try {
      const ids = await resolveRecordIds();
      const customerId = ids.customerId;
      if (customerId) {
        const snaps = await fetchBudgetSnapshotsFresh(customerId);

        const coerceToInt = (v: unknown): number | undefined => {
          if (typeof v === "number" && Number.isFinite(v)) return v;
          if (typeof v === "string") {
            const n = parseInt(v, 10);
            return Number.isNaN(n) ? undefined : n;
          }
          return undefined;
        };

        const afterHaveChildrenUnder18 =
          normalizedStep3.haveDependents === true ? "Y" : "N";
        const afterUnder16 =
          coerceToInt(normalizedStep3.under16Dependents) ?? 0;
        const afterAged16Plus =
          coerceToInt(normalizedStep3.between16to18Dependents) ?? 0;

        const before = buildProfileBudgetContext();
        const after = buildProfileBudgetContext({
          haveChildrenUnder18: afterHaveChildrenUnder18,
          dependentChildrenUnder16: afterUnder16,
          dependentChildrenAged16Plus: afterAged16Plus,
        });

        const plan = computeConfirmBeforeClearPlan({
          before,
          after,
          snaps,
          userId: Number(user.userId),
        });

        if (plan.needsConfirm) {
          setConfirmMessage(
            `This change will remove the data you entered for: ${plan.clearedLabels.join(
              ", "
            )}. Do you want to continue?`
          );
          const cid = Number(customerId);
          const uid = Number(user.userId);
          const { income, pension, expense } =
            buildClearOptionsFromConfirmPlan(plan);

          setPendingConfirmAction(async () => {
            if (hasAnyIncomeClear(income)) {
              await clearHiddenIncomeFields(cid, { ...income, userId: uid });
            }
            if (hasAnyPensionClear(pension)) {
              await clearHiddenPensionFields(cid, { ...pension, userId: uid });
            }
            if (hasAnyExpenseClear(expense)) {
              await clearHiddenExpenseFields(cid, { ...expense, userId: uid });
            }
            await doSave();
          });
          setConfirmOpen(true);
          return;
        }
      }
    } catch {
      // If anything goes wrong during gating, fall back to saving to avoid blocking the user
    }

    await doSave();
  };

  const updateSmartReviewMapping = async (fieldName: string) => {
    setSmartReviewMappings({
      ...smartReviewMappings,
      [fieldName]: smartReviewMappings[fieldName] == "Y" ? "N" : "Y",
    });

    await SmartReviewService.patchSmartReview(
      {
        [fieldName]: smartReviewMappings[fieldName] == "Y" ? "N" : "Y",
      },
      smartReviewMappings.id as string
    );
  };

  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    const fetchSmartReviewData = async (): Promise<SmartReviewEntry[]> => {
      try {
        const data = await SmartReviewService.getSmartReview(
          customerData.customerId
        );

        return data as SmartReviewEntry[];
      } catch (error) {
        console.error("Failed to fetch companies", error);
      }

      return [];
    };

    fetchSmartReviewData().then(async (data: SmartReviewEntry[]) => {
      if (data.length == 0) {
        // make new entry
        const data: Record<string, unknown> =
          await SmartReviewService.createSmartReview({
            customerId: customerData.customerId,
            valProfileAgeEmploymentMismatch: "N",
            valProfileBenefitAgeMismatch: "N",
            valDependantsBenefitMismatch: "N",
            valIncomeUnemployedSalary: "N",
            valAccommodationParentCosts: "N",
            valVehicleCostsNoVehicle: "N",
            valVehicleNoCosts: "N",
            valSavingsTooHigh: "N",
            valDebtAmountsMissing: "N",
            valIncomeSmartReviewDate: "N",
          });

        setSmartReviewMappings(data);
      } else {
        setSmartReviewMappings(data[0] as any);
      }
    });
  }, []);

  useEffect(() => {
    void refreshProfile();
  }, [user?.userId]);

  const isUnitedUtilitiesCustomer = (): boolean => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    if (!customerData) return false;
    const field5TxtFld =
      typeof customerData.field5TxtFld === "string"
        ? customerData.field5TxtFld.trim().toLowerCase()
        : "";

    return field5TxtFld.includes("united utilit");
  };

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    async function main() {
      const dobField = personalDetails.fields.find((f) => {
        return f.label == "Date of birth";
      });
      const age =
        dobField && typeof dobField.value === "string"
          ? (calculateAge(dobField.value) ?? 0)
          : 0;

      const employmentStatusField = personalDetails.fields.find((f) => {
        return f.label == "Employment status";
      });
      const employmentStatus = String(employmentStatusField?.value ?? "");

      const getIncomeVal = (label: string): number => {
        const item = incomeItems.find((item: any) => {
          return item.label == label;
        });

        const value = item ? item.amount.replace("£", "") : null;

        if (!item || isNaN(value)) {
          return 0;
        }

        return parseInt(value);
      };

      const statePension = getIncomeVal("State pension");
      const pip = getIncomeVal("Disability benefits (DLA/PIP/AA)");
      const universalCredit = getIncomeVal("Universal credit");

      const getDependantsVal = (label: string): number => {
        const item = childrenAndAdults.fields.find((field) => {
          return field.label == label;
        });

        if (!item) {
          return 0;
        }

        const value =
          typeof item.value === "string" || typeof item.value === "number"
            ? String(item.value)
            : null;

        if (!value || isNaN(Number(value))) {
          return 0;
        }

        return parseInt(value, 10);
      };

      const dependentChildrenUnder16 = getDependantsVal("Dependants under 16");
      const dependentChildrenAged16Plus = getDependantsVal(
        "Dependants between 16 - 18"
      );
      const childBenefit = getIncomeVal("Child benefit");

      const salary = getIncomeVal("Salary");

      const getFixedExpVal = (label: string): number => {
        const item = fixedExpenseList.find((item: any) => {
          return item.item.label == label;
        });

        const value = item ? item.item.amount.replace("£", "") : null;

        if (!item || isNaN(value)) {
          return 0;
        }

        return parseInt(value);
      };

      const accomodationField = personalDetails.fields.find((f) => {
        return f.label == "Accommodation Type";
      });
      const accomodation = String(accomodationField?.value ?? "");

      const rent = getFixedExpVal("Rent");
      const mortgage = getFixedExpVal("Mortgage");

      const vehicleField = propertyAndPossessions.fields.find((f) => {
        return f.label == "Number of Vehicle owned";
      });
      const haveVehicle = vehicleField
        ? parseInt(vehicleField.value as string) > 0
        : false;

      const vehicleFuel = getFixedExpVal("Fuel, parking and tolls");
      const vehicleInsurance = getFixedExpVal("Car insurance");
      const vehicleTax = getFixedExpVal("Road tax");

      //Money in savings
      const getAssetsVal = (label: string): number => {
        const item = assetItems.find((item: any) => {
          return item.label == label;
        });

        const value = item ? item.amount.replace("£", "") : null;

        if (!item || isNaN(value)) {
          return 0;
        }

        return parseInt(value);
      };

      const assetSavings = getAssetsVal("Money in savings");
      const balanceAmount = summary.balanceAmount;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const calculatedBalanceAmount = Math.max(0, summary.balanceAmount);

      const debts = [...priorityDebts, ...nonPriorityDebts];
      const hasAmountOwed = debts.some((debt) => {
        const value = debt.amountOwed.replace("£", "");

        if (isNaN(value) || parseInt(value) <= 0) {
          return false;
        }

        return true;
      });

      // UU start date benefits prompt
      // statePension, pip, universalCredit
      const jsa1 = getIncomeVal("Jobseeker's Allowance (income-based)");
      const jsa2 = getIncomeVal("Jobseeker's Allowance (contribution-based)");
      const lha = getIncomeVal("Housing Benefit Local Allowance");
      const pensionCredit = getIncomeVal("Pension Credit");

      // 1. universalCredit
      // 2. jsa1, jsa2 - Jobseeker's Allowance (income-based) / Jobseeker's Allowance (contribution-based)
      // 4. childBenefit
      // 6. pip
      // 7. lha - Housing Benefit Local Allowance
      // 8. pensionCredit

      const smartSuggestionsConditionMap = [
        {
          name: "Universal credit",
          value: universalCredit,
          fieldName: "universalCredit",
          show: false,
        },
        {
          name: "Child benefit",
          value: childBenefit,
          fieldName: "childBenefit",
          show: false,
        },
        {
          name: "Disability benefits (DLA/PIP/AA)",
          value: pip,
          fieldName: "disabilityAllowance",
          show: false,
        },
        {
          name: "Housing Benefit Local Allowance",
          value: lha,
          fieldName: "housingBenefitLocalAllowance",
          show: false,
        },
        {
          name: "Pension Credit",
          value: pensionCredit,
          fieldName: "pensionCredit",
          show: false,
        },
      ];

      const fieldStates = await uuController.refresh();

      for (const entry of smartSuggestionsConditionMap) {
        const fieldInfo = fieldStates[entry.fieldName];
        const initialMonth = fieldInfo?.initialMonth;
        const initialYear = fieldInfo?.initialYear;

        if (entry.value && !(initialMonth && initialYear)) {
          entry.show = true;
        }
      }

      const smartReviewTriggers: smartReviewTrigger[] = [
        {
          id: "VAL_PROFILE_AGE_EMPLOYMENT_MISMATCH",
          fieldName: "valProfileAgeEmploymentMismatch",
          section: "profile",
          condition:
            (age < 16 &&
              ["Full Time", "Self Employed", "Part Time"].includes(
                employmentStatus
              )) ||
            (age >= 66 && employmentStatus == "Full Time"),
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              Your age: {DynamicField({ text: age, id: "age" })} and employment
              status:{" "}
              {DynamicField({ text: employmentStatus, id: "employmentStatus" })}{" "}
              don’t usually go together. If you’re training, studying, or
              part-time, please make sure the details are correct.
            </Typography>
          ),
        },
        {
          id: "VAL_PROFILE_BENEFIT_AGE_MISMATCH",
          fieldName: "valProfileBenefitAgeMismatch",
          section: "profile",
          condition:
            (statePension || pip || universalCredit) &&
            ((age < 16 &&
              ["Full Time", "Self Employed", "Part Time"].includes(
                employmentStatus
              )) ||
              (age >= 66 && employmentStatus == "Full Time") ||
              (age < 18 && (statePension || pip || universalCredit))),
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              Some benefits like{" "}
              {DynamicField({
                text: "Universal Credit",
                id: "Universal credit",
              })}
              ,{" "}
              {DynamicField({
                text: "Disability Living Allowance & PIP, Payment & Attendance Allowance",
                id: "Disability benefits (DLA/PIP/AA)",
              })}{" "}
              ,{" "}
              {DynamicField({
                text: "State Pension",
                id: "State pension",
              })}{" "}
              don’t usually apply at your age. It might help to review your{" "}
              {DynamicField({
                text: "Benefits",
                id: "benefits",
                type: "section",
              })}{" "}
              - if it’s for someone else in your home, just add a quick note
              there.
            </Typography>
          ),
        },
        {
          id: "VAL_DEPENDANTS_BENEFIT_MISMATCH",
          fieldName: "valDependantsBenefitMismatch",
          section: "profile",
          condition:
            dependentChildrenUnder16 + dependentChildrenAged16Plus > 0 &&
            !childBenefit,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You've added that you have{" "}
              {DynamicField({
                text: dependentChildrenUnder16 + dependentChildrenAged16Plus,
                id: "dependants",
              })}{" "}
              dependents under 18, but haven't added any{" "}
              {DynamicField({ text: "Child Benefits", id: "Child benefit" })}.
            </Typography>
          ),
        },
        {
          id: "VAL_INCOME_SMART_REVIEW_DATE",
          fieldName: "valIncomeSmartReviewDate",
          section: "income",
          condition:
            isUnitedUtilitiesCustomer() &&
            smartSuggestionsConditionMap.some((e) => e.show),
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You’ve added that you receive{" "}
              {smartSuggestionsConditionMap
                .filter((e) => e.show)
                .map((e, index, arr) => (
                  <span key={e.fieldName}>
                    {DynamicField({ text: e.name, id: e.name })}
                    {index < arr.length - 1 ? ", " : ""}
                  </span>
                ))}{" "}
              but haven’t added the date you started to receive this. Would you
              like to review this?
            </Typography>
          ),
        },

        {
          id: "VAL_INCOME_Unemployed_SALARY",
          fieldName: "valIncomeUnemployedSalary",
          section: "income",
          condition:
            ["Full Time", "Self Employed", "Part Time", "Other"].includes(
              employmentStatus
            ) && !salary,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              Your employment status is{" "}
              {DynamicField({ text: employmentStatus, id: "employmentStatus" })}{" "}
              and your{" "}
              {DynamicField({ text: "take home salary", id: "Salary" })} is
              empty - would you like to review this?
            </Typography>
          ),
        },
        {
          id: "VAL_ACCOMMODATION_PARENT_COSTS",
          fieldName: "valAccommodationParentCosts",
          section: "fixed-expenses",
          condition:
            accomodation == "Living with parents" && (rent > 0 || mortgage > 0),
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You said you live with parents, but entered rent{" "}
              {DynamicField({ text: `£${rent}`, id: "rent" })} or mortgage{" "}
              {DynamicField({ text: `£${mortgage}`, id: "mortgage" })} - would
              you like to check that?
            </Typography>
          ),
        },
        {
          id: "VAL_VEHICLE_COSTS_NO_VEHICLE",
          fieldName: "valVehicleCostsNoVehicle",
          section: "assets",
          condition:
            (vehicleFuel > 0 || vehicleInsurance > 0 || vehicleTax > 0) &&
            !haveVehicle,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You’ve entered costs for{" "}
              {DynamicField({ text: "fuel", id: "vehicleFuel" })},{" "}
              {DynamicField({ text: "car insurance", id: "vehicleInsurance" })},{" "}
              {DynamicField({ text: "road tax", id: "vehicleTax" })}, but said
              you don’t {DynamicField({ text: "own a vehicle", id: "vehicle" })}{" "}
              - would you like to review that?
            </Typography>
          ),
        },
        {
          id: "VAL_VEHICLE_NO_COSTS",
          fieldName: "valVehicleNoCosts",
          section: "assets",
          condition:
            !vehicleFuel && !vehicleInsurance && !vehicleTax && haveVehicle,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You’ve told us you{" "}
              {DynamicField({ text: "own a vehicle", id: "vehicle" })}, but not
              entered costs for{" "}
              {DynamicField({ text: "fuel", id: "vehicleFuel" })},{" "}
              {DynamicField({ text: "car insurance", id: "vehicleInsurance" })},{" "}
              {DynamicField({ text: "road tax", id: "vehicleTax" })} - would you
              like to review that?
            </Typography>
          ),
        },
        {
          id: "VAL_SAVINGS_TOO_HIGH",
          fieldName: "valSavingsTooHigh",
          section: "assets",
          condition: assetSavings > balanceAmount && assetSavings != 0,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              Your {DynamicField({ text: "savings", id: "Money in savings" })}{" "}
              (from savings expenditure) seem higher than your leftover income{" "}
              {<strong>£{balanceAmount}</strong>} - is that from earlier months?
            </Typography>
          ),
        },
        {
          id: "VAL_DEBT_AMOUNTS_MISSING",
          fieldName: "valDebtAmountsMissing",
          section: "debts",
          condition: debts.length > 0 && !hasAmountOwed,
          prompt: (
            <Typography
              sx={{
                fontWeight: 400,
                color: "#4B5563",
              }}
            >
              You’ve told us you have {debts.length} debt
              {debts.length > 1 ? "s" : null}, but there are no amounts owed -
              would you like to check your{" "}
              {DynamicField({ text: "Debts", id: "debts", type: "section" })}?
            </Typography>
          ),
        },
      ];

      // for (const trigger of smartReviewTriggers) {
      //   trigger.condition = true;
      // }

      setSmartReviewTriggers(smartReviewTriggers);
    }

    main();
  }, [
    isLoaded,
    dependencyToggle,
    incomeItems,
    childrenAndAdults,
    assetItems,
    summary,
  ]);

  const hasFilledOutIe = useMemo(() => {
    const incomeHasVal = incomeItems.some((i: any) => i.amount != "-");
    const assetsHasVal = assetItems.some((i: any) => i.amount != "-");
    const fixedExpenseHasVal = fixedExpenseList.some(
      (i: any) => i.item.amount != "-"
    );
    const flexibleExpenseHasVal = flexibleExpenseList.some(
      (i: any) => i.item.amount != "-"
    );

    return (
      incomeHasVal ||
      assetsHasVal ||
      fixedExpenseHasVal ||
      flexibleExpenseHasVal ||
      priorityDebts.length > 0 ||
      nonPriorityDebts.length > 0
    );
  }, [
    incomeItems,
    assetItems,
    fixedExpenseList,
    flexibleExpenseList,
    priorityDebts,
    nonPriorityDebts,
  ]);

  const triggersCount = useMemo(() => {
    let count = 0;
    let hasArtificialResolved = false;

    for (const t of smartReviewTriggers) {
      if (t.condition && smartReviewMappings[t.fieldName] == "Y") {
        hasArtificialResolved = true;
      }

      if (t.condition && smartReviewMappings[t.fieldName] == "N") {
        count += 1;
      }
    }

    if (hasFilledOutIe) {
      if (count == 0 && hasArtificialResolved) {
        count = 202;
      }

      setTimeout(() => {
        setTriggersCount(count);
      }, 1000);
    } else {
      setTriggersCount(0);
    }

    return count;
  }, [smartReviewTriggers, smartReviewMappings, hasFilledOutIe]);

  const smartReviewData = useMemo(() => {
    const sections = [
      {
        id: "profile",
        name: "Profile",
        triggers: smartReviewTriggers.filter(
          (t) => t.section == "profile" && t.condition
        ),
      },
      {
        id: "income",
        name: "Income",
        triggers: smartReviewTriggers.filter(
          (t) => t.section == "income" && t.condition
        ),
      },
      {
        id: "fixed-expenses",
        name: "Fixed expenses",
        triggers: smartReviewTriggers.filter(
          (t) => t.section == "fixed-expenses" && t.condition
        ),
      },
      ,
      {
        id: "assets",
        name: "Assets",
        triggers: smartReviewTriggers.filter(
          (t) => t.section == "assets" && t.condition
        ),
      },
      ,
      {
        id: "debts",
        name: "Debts",
        triggers: smartReviewTriggers.filter(
          (t) => t.section == "debts" && t.condition
        ),
      },
    ];

    return sections;
  }, [smartReviewTriggers]);

  const handleDynamicFieldAction = (id: string) => {
    if (id == "employmentStatus") {
      setEmploymentDialogOpen(true);
    } else if (id == "age") {
      setDobDialogOpen(true);
    } else if (id == "vehicle") {
      setVehicleDialogOpen(true);
    }

    if (
      [
        "Universal credit",
        "Disability benefits (DLA/PIP/AA)",
        "State pension",
        "Child benefit",
        "Salary",
        "Jobseeker's Allowance (income-based)",
        "Housing Benefit Local Allowance",
        "Pension Credit",
      ].includes(id)
    ) {
      handleEditItem("income", id);
    }

    if (["Money in savings"].includes(id)) {
      handleEditItem("assets", id);
    }

    if (id == "benefits") {
      navigate(`/update-budget?outer=income&inner=benefits`);
    }

    if (id == "profile") {
      navigate(`/profile`);
    }

    if (id == "debts") {
      navigate(`/update-budget?outer=debts&inner=debts`);
    }

    if (id == "income") {
      navigate(`/update-budget?outer=income&inner=salary`);
    }

    if (id == "assets") {
      navigate(`/update-budget?outer=assets&inner=properties`);
    }

    if (id == "fixed-expenses") {
      navigate(`/update-budget?outer=fixed-expenses&inner=household`);
    }

    if (id == "dependants") {
      handleDependentsDialogOpen();
    }

    if (id == "rent") {
      setEditDialogConfig({
        groupKey: "household",
        fieldKey: "houseRent",
        title: `Edit Rent`,
      });
      safeOpenEditDialog();
    }

    if (id == "mortgage") {
      setEditDialogConfig({
        groupKey: "household",
        fieldKey: "mortgage",
        title: `Edit Mortgage`,
      });
      safeOpenEditDialog();
    }

    if (id == "vehicleFuel") {
      setEditDialogConfig({
        groupKey: "transport",
        fieldKey: "fuelParkingAndTollRoadCharges",
        title: `Edit Fuel`,
      });
      safeOpenEditDialog();
    }

    if (id == "vehicleTax") {
      setEditDialogConfig({
        groupKey: "transport",
        fieldKey: "roadTax",
        title: `Edit Road Tax`,
      });
      safeOpenEditDialog();
    }

    if (id == "vehicleInsurance") {
      setEditDialogConfig({
        groupKey: "transport",
        fieldKey: "carInsurance",
        title: `Edit Car Insurance`,
      });
      safeOpenEditDialog();
    }
  };

  const DynamicField = ({
    text,
    id,
    type = "field",
  }: {
    text: string | number;
    id: string;
    type?: string;
  }) => {
    return (
      <span
        onClick={() => {
          handleDynamicFieldAction(id);
        }}
        style={{ cursor: "pointer" }}
      >
        <Typography
          component="span"
          sx={{
            color: ThemeColours.labelVar2,
            fontWeight: 600,
            borderBottom: `1px dashed ${ThemeColours.labelVar2}`,
            marginRight: 0.5,
          }}
        >
          {text}
        </Typography>
        {type == "field" ? (
          <img src={pencilGreenIcon} alt="Pencil Icon" height="10" />
        ) : (
          <img src={OpenSectionIcon} alt="Open Section Icon" height="10" />
        )}
      </span>
    );
  };

  return (
    <>
      {/* Confirmation dialog for destructive budget clears (e.g., pension when age changes) */}
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setConfirmPendingAction(null);
          setConfirmMessage(
            "This change will remove some saved budget data. Do you want to continue?"
          );
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#003d34",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Confirm change</span>
          <IconButton
            onClick={() => {
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              setConfirmMessage(
                "This change will remove some saved budget data. Do you want to continue?"
              );
            }}
            sx={{ color: "#6b7280" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {confirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: "1px solid #e4e7eb" }}>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              setConfirmMessage(
                "This change will remove some saved budget data. Do you want to continue?"
              );
            }}
            sx={{ color: "#374151", textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            onClick={async () => {
              const fn = confirmPendingAction;
              setConfirmOpen(false);
              setConfirmPendingAction(null);
              setConfirmMessage(
                "This change will remove some saved budget data. Do you want to continue?"
              );
              if (typeof fn === "function") {
                await fn();
              }
            }}
            variant="contained"
            sx={{
              backgroundColor: "#1a5053",
              color: "#ffffff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#153f41" },
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              width: "100%",
              maxWidth: "100%",
              maxHeight: "90vh",
              borderRadius: "16px 16px 0 0",
              position: "fixed",
              bottom: 0,
            }),
            ...(!isMobile && {
              height: "100vh",
            }),
          },
        }}
        TransitionComponent={Transition as any}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <ProfileFormDialog
          open={vehicleDialogOpen}
          title="Edit vehicle details"
          schema={vehicleDetailsSchema as unknown as any}
          isSaving={savingFormId === "profile-vehicle-form"}
          initialData={(() => {
            // Schema field names: haveVehicle (boolean), noOfVehiclesInHouseholds (number)
            // Data comes from income-onboarding-step-4
            const incomeStep4 = getIncomeOnboardingStepData(4);

            // Get haveVehicle boolean
            let haveVehicle = false;
            const haveVehicleField = incomeStep4["haveVehicle"];
            if (typeof haveVehicleField === "boolean") {
              haveVehicle = haveVehicleField;
            } else if (typeof haveVehicleField === "string") {
              const hv = haveVehicleField.trim().toLowerCase();
              haveVehicle =
                hv === "y" || hv === "yes" || hv === "true" || hv === "1";
            } else {
              const apiValue = getString(customerData, "haveVehicle");
              haveVehicle = apiValue === "Y";
            }

            // Get vehicle count (robust to numeric strings)
            let num = 0;
            const numField = incomeStep4["noOfVehiclesInHouseholds"];
            if (typeof numField === "number") {
              num = numField;
            } else if (typeof numField === "string") {
              const parsed = parseInt(numField, 10);
              if (!Number.isNaN(parsed)) num = parsed;
            } else if (
              typeof customerData?.["noOfVehiclesInHouseholds"] === "number"
            ) {
              num = customerData["noOfVehiclesInHouseholds"] as number;
            } else if (
              typeof customerData?.["noOfVehiclesInHouseholds"] === "string"
            ) {
              const parsed = parseInt(
                customerData["noOfVehiclesInHouseholds"] as unknown as string,
                10
              );
              if (!Number.isNaN(parsed)) num = parsed;
            }
            // Clamp to [0, 5] and apply defaults per toggle
            const clamped = Math.min(Math.max(num, 0), 5);
            const finalCount = haveVehicle ? (clamped > 0 ? clamped : 1) : 0;

            return { haveVehicle, noOfVehiclesInHouseholds: finalCount };
          })()}
          formId="profile-vehicle-form"
          onSubmit={handleVehicleSave}
          onClose={() => setVehicleDialogOpen(false)}
        />
        <ProfileFormDialog
          open={employmentDialogOpen}
          title="Edit employment status"
          schema={employmentStatusSchema as unknown as any}
          isSaving={savingFormId === "profile-employment-form"}
          initialData={(() => {
            const incomeStep1 = getIncomeOnboardingStepData(1);
            const val =
              getString(incomeStep1, "customerEmploymentStatus") ||
              getString(customerData, "customerEmploymentStatus") ||
              "";
            return { customerEmploymentStatus: val };
          })()}
          formId="profile-employment-form"
          onSubmit={handleEmploymentSave}
          onClose={() => setEmploymentDialogOpen(false)}
        />
        <ProfileFormDialog
          open={dobDialogOpen}
          title="Edit date of birth"
          schema={dateOfBirthSchema as unknown as any}
          isSaving={savingFormId === "profile-dob-form"}
          initialData={(() => {
            const dob = getStepData(4);
            // Schema expects 'dateOfBirth' as Date object
            let dateValue: Date | undefined;

            // Try direct dateOfBirth field first
            const directDob = dob["dateOfBirth"];
            if (directDob instanceof Date) {
              dateValue = directDob;
            } else if (typeof directDob === "string") {
              dateValue = new Date(directDob);
            }

            // Fallback to customer data
            if (!dateValue || isNaN(dateValue.getTime())) {
              const dobStr = getString(customerData, "debtor1DateOfBirthStr");
              if (dobStr) {
                dateValue = new Date(dobStr);
              } else {
                const dobArray = customerData?.["debtor1DateOfBirth"];
                if (Array.isArray(dobArray) && dobArray.length === 3) {
                  const year = dobArray[0];
                  const month = dobArray[1];
                  const day = dobArray[2];
                  if (
                    typeof year === "number" &&
                    typeof month === "number" &&
                    typeof day === "number"
                  ) {
                    dateValue = new Date(year, month - 1, day);
                  }
                }
              }
            }

            return {
              dateOfBirth:
                dateValue && !isNaN(dateValue.getTime())
                  ? dateValue
                  : undefined,
            };
          })()}
          formId="profile-dob-form"
          onSubmit={handleDobSave}
          onClose={() => setDobDialogOpen(false)}
        />
        <ProfileFormDialog
          open={dependentsDialogOpen}
          title="Edit dependents details"
          schema={dependentsDetailsSchema as unknown as any}
          initialData={dependentsInitialData || {}}
          isSaving={savingFormId === "profile-dependents-form"}
          formId="profile-dependents-form"
          onSubmit={handleDependentsSave}
          onClose={() => setDependentsDialogOpen(false)}
        />

        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Dialog Title */}
          <DialogTitle
            sx={{
              borderBottom: "1px solid #e0e0e0",
              px: 4,
              justifyContent: "space-between",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: ThemeColours.titleVar1,
                fontSize: 22,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              Smart review
              <CircleNumber
                number={normaliseCount(triggersCount)}
                {...(ThemeColours.neon1 && { bgColor: ThemeColours.neon1 })}
                size={20}
                {...(ThemeColours.labelVar2 && {
                  color: ThemeColours.labelVar2,
                })}
              />
            </Typography>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme: any) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* Dialog Content */}
          <DialogContent sx={{ pt: 3, px: 4, flexGrow: 1, overflowY: "auto" }}>
            <Typography
              variant="body1"
              sx={{ my: 2, color: ThemeColours.subtitleVar1 }}
            >
              Please review the information you've provided. You can edit any
              section before continuing to the next part of your budget.
            </Typography>

            <Box>
              {smartReviewData.map((section: any) => {
                return section.triggers.length > 0 ? (
                  <React.Fragment key={section.name}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span
                        onClick={() => {
                          handleDynamicFieldAction(section.id);
                        }}
                        style={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: 5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: "#0D3D3D",
                            fontSize: 22,
                          }}
                        >
                          {section?.name}
                        </Typography>

                        <img
                          src={OpenSectionBlack}
                          alt="Open Section Icon"
                          height="15"
                        />
                      </span>
                    </Box>

                    {section.triggers.map((t: any) => {
                      return t.condition ? (
                        <Box
                          key={t.id}
                          sx={{
                            gap: 2,
                            px: 1.5,
                            mb: 2,
                            backgroundColor: "#fff",
                            border: (theme) =>
                              `0px solid ${theme.palette.divider}`,
                            borderLeft: "4px solid red",
                            borderRadius: 0,
                          }}
                        >
                          {t.prompt}

                          <Button
                            onClick={() => {
                              updateSmartReviewMapping(t.fieldName);
                            }}
                            sx={{
                              height: "20px",
                              paddingY: 2,
                              paddingX: 1.5,
                              mt: 1,
                              backgroundColor:
                                smartReviewMappings[t.fieldName] == "Y"
                                  ? "#DBEAFE"
                                  : "#f3f4f6",
                              borderRadius: "50px",
                              color:
                                smartReviewMappings[t.fieldName] == "Y"
                                  ? "#2562EB"
                                  : "#1f2937",
                              fontSize: "16px",
                              fontWeight: 400,
                              textTransform: "none",
                              border: "none",
                              "&:hover": {
                                backgroundColor:
                                  smartReviewMappings[t.fieldName] == "Y"
                                    ? "#DBEAFE"
                                    : "#e5e7eb",
                                border: "none",
                              },
                              "&:disabled": {
                                backgroundColor: "#f3f4f6",
                                color: "#9ca3af",
                              },

                              minWidth: 100,
                            }}
                          >
                            Ignore
                          </Button>
                        </Box>
                      ) : null;
                    })}
                  </React.Fragment>
                ) : null;
              })}
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 4,
              py: 2,
              borderTop: "1px solid #e0e0e0",
              position: "sticky",
              bottom: 0,
              backgroundColor: "white",
              zIndex: 1,
              boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                textAlign: "left",
                color: "#4b5563",
                fontSize: "16px",
                maxWidth: "70%",
              }}
            >
              Need to make changes? Just click the Edit button next to any
              section, or click
              <strong> directly on linked text to update it instantly.</strong>
            </Typography>

            <Button
              onClick={onClose}
              sx={{
                height: "45px",
                backgroundColor: "#f3f4f6",
                borderRadius: "10px",
                color: "#1f2937",
                fontSize: "18px",
                fontWeight: 600,
                textTransform: "none",
                border: "none",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  border: "none",
                },
                "&:disabled": {
                  backgroundColor: "#f3f4f6",
                  color: "#9ca3af",
                },
                minWidth: 100,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default SmartReviewDialog;
